import { useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { HiOutlineChevronDown, HiOutlineChevronRight } from 'react-icons/hi2';
import {
  RunHealthStatus,
  RunHealthObj,
  AntSensorHealth,
  GpsHealth,
  GateHealth,
  RouteHealth,
  DiffSensorStatus,
  DiffChannelHealth,
  WindSensorHealth,
  SensorInfoObj,
  ProcessingStage,
  getStageName,
  SensorOffsetsScope,
  WindSensor5ZeroOverride,
} from 'types/Run';
import { updateRun, updateRunsBulk } from 'api/runs';

interface RunHealthCheckContext {
  run?: any;
  sessionData?: any;
}

// --- Helpers ---

function healthStatusColor(status: number | null | undefined): string {
  switch (status) {
    case RunHealthStatus.GOOD:
      return 'cr-bg-green-500';
    case RunHealthStatus.AVERAGE:
      return 'cr-bg-yellow-500';
    case RunHealthStatus.WARNING:
      return 'cr-bg-orange-500';
    case RunHealthStatus.ERROR:
      return 'cr-bg-red-500';
    default:
      return 'cr-bg-gray-400';
  }
}

function healthStatusTextColor(status: number | null | undefined): string {
  switch (status) {
    case RunHealthStatus.GOOD:
      return 'cr-text-green-700';
    case RunHealthStatus.AVERAGE:
      return 'cr-text-yellow-700';
    case RunHealthStatus.WARNING:
      return 'cr-text-orange-700';
    case RunHealthStatus.ERROR:
      return 'cr-text-red-700';
    default:
      return 'cr-text-gray-500';
  }
}

function presenceDot(present: boolean) {
  return (
    <span
      className={`cr-inline-block cr-h-2.5 cr-w-2.5 cr-rounded-full ${present ? 'cr-bg-green-500' : 'cr-bg-red-400'}`}
      title={present ? 'Present' : 'Not present'}
    />
  );
}

function formatPercent(fraction: number | null | undefined): string {
  if (fraction == null) return '--';
  return `${(fraction * 100).toFixed(0)}%`;
}

function formatTime(seconds: number | null | undefined): string {
  if (seconds == null) return '--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}m${secs.toString().padStart(2, '0')}s`;
}

function formatNumber(value: number | null | undefined, decimals = 1): string {
  if (value == null) return '--';
  return value.toFixed(decimals);
}

// --- Sub-components ---

function HealthStatusBadge({ healthObj }: { healthObj: RunHealthObj }) {
  const status = healthObj.health_status;
  const name = healthObj.health_status_name;
  return (
    <div className="cr-flex cr-items-center cr-gap-3">
      <span className={`cr-inline-block cr-h-4 cr-w-4 cr-rounded-full ${healthStatusColor(status)}`} />
      <span className={`cr-text-lg cr-font-bold cr-uppercase ${healthStatusTextColor(status)}`}>{name}</span>
    </div>
  );
}

function ExpandableCard({
  title,
  present,
  summary,
  children,
  statusColor,
}: {
  title: string;
  present: boolean;
  summary: string;
  children?: React.ReactNode;
  statusColor?: string;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="cr-flex cr-w-full cr-flex-col cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-md md:cr-w-56 cr-cursor-pointer"
      onClick={() => setExpanded(!expanded)}
    >
      <div className="cr-flex cr-items-center cr-justify-between cr-mb-2">
        <span className="font-montserrat cr-text-sm cr-font-bold cr-uppercase cr-text-blue-900">{title}</span>
        {children ? (
          expanded ? (
            <HiOutlineChevronDown className="cr-h-4 cr-w-4 cr-text-gray-400" />
          ) : (
            <HiOutlineChevronRight className="cr-h-4 cr-w-4 cr-text-gray-400" />
          )
        ) : null}
      </div>
      <div className="cr-flex cr-items-center cr-gap-2 cr-text-sm cr-text-blue-900">
        <span
          className={`cr-inline-block cr-h-2.5 cr-w-2.5 cr-rounded-full ${statusColor ?? (present ? 'cr-bg-green-500' : 'cr-bg-red-400')}`}
          title={present ? 'Present' : 'Not present'}
        />
        <span>{present ? 'Present' : 'Not present'}</span>
      </div>
      {present && <div className="cr-mt-1 cr-text-xs cr-text-gray-500">{summary}</div>}
      {expanded && children && <div className="cr-mt-3 cr-border-t cr-pt-3 cr-text-xs cr-text-blue-900">{children}</div>}
    </div>
  );
}

function SensorInfoSection({ sensorInfo, showAntId = true }: { sensorInfo?: SensorInfoObj; showAntId?: boolean }) {
  if (!sensorInfo) return null;
  const { ant_device_number, ant_id_not_found_reason, battery_voltage, firmware_version } = sensorInfo;
  const hasAntId = showAntId && (ant_device_number != null || ant_id_not_found_reason != null);
  if (!hasAntId && battery_voltage == null && firmware_version == null) return null;
  return (
    <div className="cr-mt-2 cr-border-t cr-border-dashed cr-pt-2 cr-space-y-1">
      {showAntId && ant_device_number != null && <div>ANT ID: {ant_device_number}</div>}
      {showAntId && ant_device_number == null && ant_id_not_found_reason && (
        <div className="cr-text-orange-500">ANT ID: <span className="cr-italic">{ant_id_not_found_reason}</span></div>
      )}
      {battery_voltage != null && <div>Battery: {battery_voltage}V</div>}
      {firmware_version != null && <div>Firmware: v{firmware_version}</div>}
    </div>
  );
}

function AntSensorCard({
  title,
  sensor,
  sensorInfo,
  children,
  statusColor,
}: {
  title: string;
  sensor: AntSensorHealth;
  sensorInfo?: SensorInfoObj;
  children?: React.ReactNode;
  statusColor?: string;
}) {
  const antIdSummary = sensorInfo?.ant_device_number != null
    ? `ANT ID: ${sensorInfo.ant_device_number}`
    : sensorInfo?.ant_id_not_found_reason
      ? `ANT ID: ${sensorInfo.ant_id_not_found_reason}`
      : null;
  const summaryText = sensor.present
    ? antIdSummary ?? 'ANT ID missing'
    : '';

  return (
    <ExpandableCard
      title={title}
      present={sensor.present}
      summary={summaryText}
      statusColor={statusColor}
    >
      <div className="cr-space-y-1">
        <div>Packets: {sensor.packet_count ?? '--'}</div>
        <div>Sequential: {sensor.packet_count_sequential ? 'Yes' : 'No'}</div>
        <div>Dropped: {sensor.packets_dropped}</div>
        <div>Dropped fraction: {formatPercent(sensor.packets_dropped_fraction)}</div>
        <div>Collisions: {sensor.packets_collision}</div>
        {sensor.time != null && <div>Time: {formatTime(sensor.time)}</div>}
        {children}
        <SensorInfoSection sensorInfo={sensorInfo} showAntId={false} />
      </div>
    </ExpandableCard>
  );
}

function GpsSensorCard({ gps }: { gps: GpsHealth }) {
  return (
    <ExpandableCard
      title="GPS"
      present={gps.present}
      summary={gps.present ? `Acc: ${formatNumber(gps.accuracy_p90)}m` : ''}
    >
      <div className="cr-space-y-1">
        <div>Accuracy P90: {formatNumber(gps.accuracy_p90)}m</div>
        <div>Packets: {gps.packet_count ?? '--'}</div>
        <div>Duration: {formatTime(gps.time_seconds)}</div>
        {gps.pct_under_4m != null && (
          <div className="cr-mt-2 cr-border-t cr-border-dashed cr-pt-2">
            <div className="cr-font-semibold cr-mb-1">Accuracy Distribution</div>
            <div>&lt;4m: {formatPercent(gps.pct_under_4m)}</div>
            <div>4–8m: {formatPercent(gps.pct_4_to_8m)}</div>
            <div>8–20m: {formatPercent(gps.pct_8_to_20m)}</div>
            <div>&gt;20m: {formatPercent(gps.pct_over_20m)}</div>
          </div>
        )}
      </div>
    </ExpandableCard>
  );
}

function GatesSensorCard({ gates, healthObj }: { gates: GateHealth; healthObj: RunHealthObj }) {
  // Support both new merged format and old separate gate_intersection
  const intersects = gates.route_and_gates_intersect ?? healthObj.gate_intersection?.route_and_gates_intersect ?? null;
  const definedCount = gates.gates_defined_count ?? healthObj.gate_intersection?.gates_defined_count ?? 0;
  const crossedCount = gates.gates_crossed_count ?? healthObj.gate_intersection?.gates_crossed_count ?? 0;

  return (
    <ExpandableCard
      title="Gates"
      present={gates.present}
      summary={gates.present ? `${crossedCount}/${definedCount} gates crossed` : ''}
    >
      <div className="cr-space-y-1">
        {gates.gate_a_lat != null && (
          <div>
            Gate A: {formatNumber(gates.gate_a_lat, 5)}, {formatNumber(gates.gate_a_lon, 5)}
          </div>
        )}
        {gates.gate_b_lat != null && (
          <div>
            Gate B: {formatNumber(gates.gate_b_lat, 5)}, {formatNumber(gates.gate_b_lon, 5)}
          </div>
        )}
        {gates.gates_lat && gates.gates_lon && (
          <div className="cr-mt-2 cr-border-t cr-border-dashed cr-pt-2">
            <div className="cr-font-semibold cr-mb-1">Session Gates ({gates.gates_lat.length})</div>
            {gates.gates_lat.map((lat, i) => (
              <div key={i}>
                {i}: {formatNumber(lat, 5)}, {formatNumber(gates.gates_lon?.[i], 5)}
              </div>
            ))}
          </div>
        )}
        <div className="cr-flex cr-items-center cr-gap-2 cr-mt-1">
          {presenceDot(intersects === true)}
          <span>{intersects ? 'Intersects' : 'No intersection'}</span>
        </div>
      </div>
    </ExpandableCard>
  );
}

function RouteSensorCard({ route }: { route: RouteHealth }) {
  const lengthKm = route.route_length_m != null ? (route.route_length_m / 1000).toFixed(2) : '--';
  return (
    <ExpandableCard
      title="Route"
      present={route.present}
      summary={route.present ? `${lengthKm} km, ${route.bearing_deg != null ? `${route.bearing_deg}°` : '--'}` : ''}
    >
      <div className="cr-space-y-1">
        <div>Length: {lengthKm} km</div>
        <div>Bearing: {route.bearing_deg != null ? `${route.bearing_deg}°` : '--'}</div>
        <div>Segments: {route.segment_count ?? '--'}</div>
        {route.start_lat != null && (
          <div>
            Start: {formatNumber(route.start_lat, 5)}, {formatNumber(route.start_lon, 5)}
          </div>
        )}
        {route.end_lat != null && (
          <div>
            End: {formatNumber(route.end_lat, 5)}, {formatNumber(route.end_lon, 5)}
          </div>
        )}
      </div>
    </ExpandableCard>
  );
}



function DiffChannelRow({ label, ch }: { label: string; ch: DiffChannelHealth }) {
  if (ch.status === DiffSensorStatus.UNKNOWN) {
    return (
      <div className="cr-mb-3">
        <span className="cr-font-medium">{label}:</span>{' '}
        <span className="cr-text-gray-400">Unknown</span>
      </div>
    );
  }

  const maxStd = Math.max(ch.start_std_pa ?? 0, ch.end_std_pa ?? 0);
  const isStable = maxStd < 1.0;
  const ref = ch.reference_pa;

  // Assess autocal quality independently of measurement stability
  let autocalGood = true;
  if (ref != null && ch.start_median_pa != null && ch.end_median_pa != null) {
    autocalGood =
      Math.abs(ch.start_median_pa - ref) <= 1.0 && Math.abs(ch.end_median_pa - ref) <= 1.0;
  }

  // Circle color and status text
  let circleColor: string;
  let statusText: string;

  if (ch.status === DiffSensorStatus.ERROR) {
    circleColor = 'cr-bg-red-500';
    statusText = 'Valid Measurement, Measurement Drift';
  } else if (!isStable) {
    circleColor = 'cr-bg-gray-400';
    statusText = `Unstable Measurement, ${autocalGood ? 'Good' : 'Needs'} Autocal`;
  } else if (!autocalGood) {
    circleColor = 'cr-bg-orange-500';
    statusText = 'Valid Measurement, Needs Autocal';
  } else {
    circleColor = 'cr-bg-green-500';
    statusText = 'Valid Measurement, Good Autocal';
  }

  return (
    <div className="cr-mb-3">
      <div>
        <span className="cr-font-medium">{label}:</span>{' '}
        Measurement (σ: {formatNumber(maxStd, 2)}, {isStable ? 'good' : 'unstable'}{' '}
        {isStable ? '<' : '>'} 1 Pa)
      </div>
      <div className="cr-pl-14">
        Zero Ref (ref {ref != null ? formatNumber(ref, 2) : '--'}, start{' '}
        {formatNumber(ch.start_median_pa)} → end {formatNumber(ch.end_median_pa)} Pa)
      </div>
      <div className="cr-pl-14 cr-flex cr-items-center cr-gap-1.5">
        <span className={`cr-inline-block cr-h-2.5 cr-w-2.5 cr-rounded-full ${circleColor}`} />
        <span>{statusText}</span>
      </div>
    </div>
  );
}

function ProbeHealthRow({ probe }: { probe: NonNullable<WindSensorHealth['probe_health']> }) {
  const mean = probe.mean_abs_dev;
  const status = probe.status;
  const color = healthStatusColor(status);
  const label =
    status === RunHealthStatus.GOOD
      ? 'Good'
      : status === RunHealthStatus.WARNING
        ? 'Elevated — probe may be partially blocked'
        : status === RunHealthStatus.ERROR
          ? 'High — probe likely blocked'
          : 'Unknown';

  return (
    <div className="cr-mt-2 cr-border-t cr-border-dashed cr-pt-2">
      <div className="cr-font-semibold cr-mb-1">Probe Health</div>
      <div>Mean |Px2PsDev|: {mean != null ? formatNumber(mean, 3) : '--'}</div>
      <div className="cr-text-gray-400">
        Samples: {probe.n_samples ?? '--'} (DynPres &gt; 10 Pa)
      </div>
      <div className="cr-flex cr-items-center cr-gap-1.5 cr-mt-1">
        <span className={`cr-inline-block cr-h-2.5 cr-w-2.5 cr-rounded-full ${color}`} />
        <span>{label}</span>
      </div>
    </div>
  );
}

// --- Sensor Zero Override Panel ---

type ChannelMode = 'auto' | 'manual';

interface ChannelOverrideState {
  checked: boolean;
  mode: ChannelMode;
  manualValue: string;
}

function getSessionRunIds(
  sessionData: any,
  anchorRunTs: string,
  scope: SensorOffsetsScope,
): string[] {
  if (!sessionData?.configs) return [];
  const allRuns: any[] = [];
  for (const config of sessionData.configs) {
    if (config.runs) {
      for (const r of config.runs) {
        allRuns.push(r);
      }
    }
  }
  const anchorTs = new Date(anchorRunTs).getTime();
  switch (scope) {
    case 'this':
      return [];
    case 'after':
      return allRuns
        .filter((r) => new Date(r.run_ts).getTime() >= anchorTs)
        .map((r) => r.id);
    case 'before':
      return allRuns
        .filter((r) => new Date(r.run_ts).getTime() <= anchorTs)
        .map((r) => r.id);
    case 'all':
      return allRuns.map((r) => r.id);
  }
}

function SensorZeroOverridePanel({
  wsh,
  run,
  sessionData,
  onSaved,
}: {
  wsh: WindSensorHealth;
  run: any;
  sessionData?: any;
  onSaved: () => void;
}) {
  const channels: [string, DiffChannelHealth, keyof WindSensor5ZeroOverride][] = [
    ['Diff 1', wsh.diff1, 'diff1_zero_pa'],
    ['Diff 2', wsh.diff2, 'diff2_zero_pa'],
    ['Diff 3', wsh.diff3, 'diff3_zero_pa'],
  ];
  const needsAutocalChannels = channels.filter(
    ([, ch]) => ch.status === DiffSensorStatus.NEEDS_AUTOCAL,
  );

  const [channelStates, setChannelStates] = useState<Record<string, ChannelOverrideState>>(
    () => {
      const init: Record<string, ChannelOverrideState> = {};
      for (const [, , key] of needsAutocalChannels) {
        init[key] = { checked: true, mode: 'auto', manualValue: '' };
      }
      return init;
    },
  );
  const [saving, setSaving] = useState(false);

  const updateChannel = useCallback(
    (key: string, patch: Partial<ChannelOverrideState>) => {
      setChannelStates((prev) => ({
        ...prev,
        [key]: { ...prev[key], ...patch },
      }));
    },
    [],
  );

  const anyChecked = Object.values(channelStates).some((s) => s.checked);
  const hasSession = !!sessionData?.configs;

  const buildOverride = (): WindSensor5ZeroOverride => {
    const ov: WindSensor5ZeroOverride = { source: 'health_check_start_median' };
    for (const [, ch, key] of needsAutocalChannels) {
      const st = channelStates[key];
      if (!st?.checked) continue;
      if (st.mode === 'auto') {
        (ov as any)[key] = ch.start_median_pa;
      } else {
        const val = parseFloat(st.manualValue);
        if (!isFinite(val)) continue;
        (ov as any)[key] = val;
        ov.source = 'manual';
      }
    }
    return ov;
  };

  const handleApply = async (scope: SensorOffsetsScope) => {
    const override = buildOverride();
    const hasValues = needsAutocalChannels.some(([, , key]) => {
      return (override as any)[key] != null;
    });
    if (!hasValues) {
      toast.error('No valid values to apply');
      return;
    }

    const sensorOffsets = {
      version: 1,
      wind_sensor_5: override,
    };

    if (scope === 'this') {
      setSaving(true);
      try {
        const existing = run.sensor_offsets || {};
        const merged = {
          ...existing,
          ...sensorOffsets,
          wind_sensor_5: {
            ...(existing.wind_sensor_5 || {}),
            ...override,
          },
        };
        await updateRun(run.id, { id: run.id, sensor_offsets: merged });
        toast.success('Sensor zero override saved');
        onSaved();
      } catch {
        toast.error('Failed to save sensor zero override');
      } finally {
        setSaving(false);
      }
      return;
    }

    const targetIds = getSessionRunIds(sessionData, run.run_ts, scope);
    if (targetIds.length === 0) {
      toast.error('No runs found for the selected scope');
      return;
    }

    const scopeLabel = scope === 'after' ? 'this and later'
      : scope === 'before' ? 'this and earlier' : 'all';
    const ok = window.confirm(
      `Apply zero override to ${targetIds.length} run(s) `
      + `(${scopeLabel} in this session)?`,
    );
    if (!ok) return;

    setSaving(true);
    try {
      const bodies = targetIds.map((id) => ({
        id,
        sensor_offsets: sensorOffsets,
      }));
      await updateRunsBulk(bodies);
      toast.success(`Sensor zero override applied to ${targetIds.length} run(s)`);
      onSaved();
    } catch {
      toast.error('Failed to apply bulk sensor zero override');
    } finally {
      setSaving(false);
    }
  };

  if (needsAutocalChannels.length === 0) return null;

  return (
    <div className="cr-mt-3 cr-border-t cr-border-dashed cr-pt-3">
      <div className="cr-font-semibold cr-mb-2">Apply New Zero</div>
      {needsAutocalChannels.map(([label, ch, key]) => {
        const st = channelStates[key];
        if (!st) return null;
        return (
          <div key={key} className="cr-mb-2 cr-flex cr-items-center cr-gap-2">
            <input
              type="checkbox"
              checked={st.checked}
              onChange={(e) => updateChannel(key, { checked: e.target.checked })}
            />
            <span className="cr-font-medium cr-w-12">{label}</span>
            <select
              className="cr-text-xs cr-border cr-rounded cr-px-1 cr-py-0.5"
              value={st.mode}
              onChange={(e) => updateChannel(key, {
                mode: e.target.value as ChannelMode,
              })}
            >
              <option value="auto">Auto</option>
              <option value="manual">Manual</option>
            </select>
            {st.mode === 'auto' ? (
              <span className="cr-text-xs cr-text-gray-500">
                {formatNumber(ch.start_median_pa, 2)} Pa
              </span>
            ) : (
              <input
                type="number"
                step="0.01"
                className="cr-text-xs cr-border cr-rounded cr-px-1 cr-py-0.5 cr-w-20"
                placeholder="Pa"
                value={st.manualValue}
                onChange={(e) => updateChannel(key, { manualValue: e.target.value })}
              />
            )}
            <span className="cr-text-xs cr-text-gray-400">
              (ref: {formatNumber(ch.reference_pa, 2)} Pa)
            </span>
          </div>
        );
      })}
      <div className="cr-flex cr-flex-wrap cr-gap-2 cr-mt-2">
        <button
          className="cr-text-xs cr-px-2 cr-py-1 cr-rounded cr-bg-blue-600
            cr-text-white disabled:cr-opacity-40"
          disabled={!anyChecked || saving}
          onClick={() => handleApply('this')}
        >
          Update this run
        </button>
        <button
          className="cr-text-xs cr-px-2 cr-py-1 cr-rounded cr-bg-blue-500
            cr-text-white disabled:cr-opacity-40"
          disabled={!anyChecked || !hasSession || saving}
          onClick={() => handleApply('after')}
        >
          Update &amp; runs after
        </button>
        <button
          className="cr-text-xs cr-px-2 cr-py-1 cr-rounded cr-bg-blue-500
            cr-text-white disabled:cr-opacity-40"
          disabled={!anyChecked || !hasSession || saving}
          onClick={() => handleApply('before')}
        >
          Update &amp; runs before
        </button>
        <button
          className="cr-text-xs cr-px-2 cr-py-1 cr-rounded cr-bg-blue-500
            cr-text-white disabled:cr-opacity-40"
          disabled={!anyChecked || !hasSession || saving}
          onClick={() => handleApply('all')}
        >
          Update all runs
        </button>
      </div>
      <div className="cr-text-xs cr-text-gray-400 cr-mt-1">
        Reprocess to apply the new zero values.
      </div>
    </div>
  );
}

function WindSensorHealthSection({
  wsh,
  run,
  sessionData,
  onSaved,
}: {
  wsh: WindSensorHealth;
  run?: any;
  sessionData?: any;
  onSaved?: () => void;
}) {
  const channels = [wsh.diff1, wsh.diff2, wsh.diff3];
  const hasUnstable = channels.some((ch) => ch.status === DiffSensorStatus.UNSTABLE);
  const hasNeedsAutocal = channels.some(
    (ch) => ch.status === DiffSensorStatus.NEEDS_AUTOCAL,
  );

  return (
    <div className="cr-mt-2 cr-border-t cr-border-dashed cr-pt-2">
      <div className="cr-font-semibold cr-mb-2">Zero Comparison</div>
      <DiffChannelRow label="Diff 1" ch={wsh.diff1} />
      <DiffChannelRow label="Diff 2" ch={wsh.diff2} />
      <DiffChannelRow label="Diff 3" ch={wsh.diff3} />
      {hasNeedsAutocal && (
        <div className="cr-mt-1 cr-text-orange-600 cr-text-xs">
          Rezero recommended — one or more channels have drifted from the reference.
        </div>
      )}
      {hasNeedsAutocal && run && onSaved && (
        <SensorZeroOverridePanel
          wsh={wsh}
          run={run}
          sessionData={sessionData}
          onSaved={onSaved}
        />
      )}
      {hasUnstable && (
        <div className="cr-mt-1 cr-text-gray-500 cr-text-xs cr-italic">
          Measurements are taken in first and last 5s of sampling. Stable measurements
          may not be possible on windy days without shielding.
        </div>
      )}
      {wsh.probe_health && <ProbeHealthRow probe={wsh.probe_health} />}
    </div>
  );
}


// --- Fallback for old format ---

function FallbackHealthDisplay({ runHealth }: { runHealth: number | null | undefined }) {
  const label =
    runHealth === RunHealthStatus.GOOD
      ? 'Good'
      : runHealth === RunHealthStatus.WARNING
        ? 'Warning'
        : runHealth === RunHealthStatus.ERROR
          ? 'Error'
          : runHealth === RunHealthStatus.AVERAGE
            ? 'Average'
            : runHealth != null
              ? `Status ${runHealth}`
              : 'No health data';

  return (
    <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md">
      <div className="cr-flex cr-items-center cr-gap-3">
        <span className={`cr-inline-block cr-h-4 cr-w-4 cr-rounded-full ${healthStatusColor(runHealth)}`} />
        <span className={`cr-text-lg cr-font-bold ${healthStatusTextColor(runHealth)}`}>{label}</span>
      </div>
      <p className="cr-mt-2 cr-text-sm cr-text-gray-400">Detailed health data is not available for this run.</p>
    </div>
  );
}

// --- Main Component ---

export default function RunHealthCheck() {
  const { run, sessionData } = useOutletContext<RunHealthCheckContext>();
  const queryClient = useQueryClient();

  const invalidateRun = useCallback(() => {
    if (run?.id) {
      queryClient.invalidateQueries({
        queryKey: ['runAndCalibration', run.id],
      });
    }
  }, [queryClient, run?.id]);

  if (!run) {
    return <div className="cr-text-gray-400 cr-p-4">Loading...</div>;
  }

  const healthObj = run.run_health_obj as RunHealthObj | null | undefined;

  // Detect new-format health object by presence of health_status_name
  const isNewFormat = healthObj && 'health_status_name' in healthObj;

  if (!isNewFormat) {
    return <FallbackHealthDisplay runHealth={run.run_health} />;
  }

  const sensors = healthObj.sensors_present;
  // Support both new 'wind' key and legacy 'aero' key
  const windSensor = sensors.wind ?? sensors.aero;
  const sensorInfo = healthObj.sensor_info;

  return (
    <div className="cr-space-y-6">
      {/* Overall Status */}
      <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md">
        <HealthStatusBadge healthObj={healthObj} />
        {run.health_code != null && (
          <p className="cr-text-sm cr-text-gray-300">
            Pipeline: {getStageName(run.health_code)}
            {run.health_code < ProcessingStage.COMPLETE && ' (incomplete)'}
          </p>
        )}
        {healthObj.total_run_time_seconds != null && (
          <div className="cr-mt-1 cr-text-sm cr-text-blue-900">
            Run time: {formatTime(healthObj.total_run_time_seconds)}
          </div>
        )}
        {healthObj.missing_sensors.length > 0 && (
          <div className="cr-mt-2 cr-text-sm cr-text-gray-500">
            Missing sensors: {healthObj.missing_sensors.join(', ')}
          </div>
        )}
        {healthObj.missing_sensors.length === 0 && (
          <div className="cr-mt-2 cr-text-sm cr-text-green-600">All sensors present</div>
        )}
      </div>

      {/* ANT Sensor Cards */}
      <div>
        <div className="cr-mb-2 cr-text-xs cr-font-semibold cr-uppercase cr-text-gray-400 cr-tracking-wide">
          ANT Sensors
        </div>
        <div className="cr-flex cr-flex-wrap cr-gap-4">
          <AntSensorCard title="Power Meter" sensor={sensors.power_meter} sensorInfo={sensorInfo?.power_meter} />
          {windSensor && (() => {
            const wsh = healthObj.wind_sensor_health;
            const needsAutocal = wsh && [wsh.diff1, wsh.diff2, wsh.diff3].some(
              (ch) => ch.status === DiffSensorStatus.NEEDS_AUTOCAL,
            );
            const probeStatus = wsh?.probe_health?.status;
            const probeBad = probeStatus === RunHealthStatus.ERROR;
            const probeWarn = probeStatus === RunHealthStatus.WARNING;
            const statusColor = probeBad
              ? 'cr-bg-red-500'
              : (needsAutocal || probeWarn)
                ? 'cr-bg-orange-500'
                : undefined;
            return (
              <AntSensorCard
                title="Wind"
                sensor={windSensor}
                sensorInfo={sensorInfo?.wind}
                statusColor={statusColor}
              >
                {wsh && (
                  <WindSensorHealthSection
                    wsh={wsh}
                    run={run}
                    sessionData={sessionData}
                    onSaved={invalidateRun}
                  />
                )}
              </AntSensorCard>
            );
          })()}
          <AntSensorCard title="Speed" sensor={sensors.speed} sensorInfo={sensorInfo?.speed}>
            {healthObj.speed_health && (
              <div className="cr-mt-2 cr-border-t cr-border-dashed cr-pt-2 cr-space-y-1">
                <div className="cr-font-semibold cr-mb-1">Speed Health</div>
                <div>
                  Wheel skips: {healthObj.speed_health.wheel_skip_count ?? '--'}
                  {healthObj.speed_health.wheel_skip_ratio != null &&
                    ` (${formatPercent(healthObj.speed_health.wheel_skip_ratio)})`}
                </div>
                <div>
                  GPS/Wheel CRC in tolerance: {formatPercent(healthObj.speed_health.gps_wheel_crc_in_tolerance)}
                  <span className="cr-text-gray-400 cr-ml-1">
                    (tol: {formatPercent(healthObj.speed_health.tolerance)})
                  </span>
                </div>
              </div>
            )}
          </AntSensorCard>
          {sensors.power_meter_2 && (
            <AntSensorCard title="Power Meter 2" sensor={sensors.power_meter_2} sensorInfo={sensorInfo?.power_meter_2} />
          )}
          {sensors.power_meter_3 && (
            <AntSensorCard title="Power Meter 3" sensor={sensors.power_meter_3} sensorInfo={sensorInfo?.power_meter_3} />
          )}
          {sensors.speed_2 && (
            <AntSensorCard title="Speed 2" sensor={sensors.speed_2} sensorInfo={sensorInfo?.speed_2} />
          )}
          {sensors.speed_3 && (
            <AntSensorCard title="Speed 3" sensor={sensors.speed_3} sensorInfo={sensorInfo?.speed_3} />
          )}
          {sensors.body && (
            <AntSensorCard title="Body" sensor={sensors.body} sensorInfo={sensorInfo?.body}>
              {(sensors.body.ref_chest_cm != null
                || sensors.body.ref_head_cm != null
                || sensors.body.ref_waist_cm != null) && (
                <div className="cr-mt-2 cr-border-t cr-border-dashed cr-pt-2">
                  <div className="cr-font-semibold cr-mb-1">Body Refs</div>
                  {sensors.body.ref_chest_cm != null && <div>Chest: {sensors.body.ref_chest_cm.toFixed(1)} cm</div>}
                  {sensors.body.ref_head_cm != null && <div>Head: {sensors.body.ref_head_cm.toFixed(1)} cm</div>}
                  {sensors.body.ref_waist_cm != null && <div>Waist: {sensors.body.ref_waist_cm.toFixed(1)} cm</div>}
                </div>
              )}
            </AntSensorCard>
          )}
        </div>
      </div>

      {/* Non-radio Sensors */}
      <div>
        <div className="cr-mb-2 cr-text-xs cr-font-semibold cr-uppercase cr-text-gray-400 cr-tracking-wide">
          Other Sensors
        </div>
        <div className="cr-flex cr-flex-wrap cr-gap-4">
          <GpsSensorCard gps={sensors.gps} />
          <GatesSensorCard gates={sensors.gates} healthObj={healthObj} />
          <RouteSensorCard route={sensors.route} />
        </div>
      </div>

    </div>
  );
}
